import { Link } from '@remix-run/react';
import { useRef, useState } from 'react';

interface SubCategoryIconsProps {
    categories: Array<{
        id: string;
        name: string;
        slug: string;
        featuredAsset?: {
            id: string;
            preview: string;
        } | null;
    }>;
    activeSlug?: string;
}

export function SubCategoryIcons({ categories, activeSlug }: SubCategoryIconsProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [moved, setMoved] = useState(false);

    if (!categories || categories.length === 0) {
        return null;
    }

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setMoved(false);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        if (Math.abs(x - startX) > 5) {
            setMoved(true);
        }
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    return (
        <div
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            className="overflow-x-auto [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing mb-4"
            style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
        >
            <div className="flex gap-4 pb-2">
                {categories.map((category) => {
                    const isActive = category.slug === activeSlug;
                    return (
                        <Link
                            key={category.id}
                            to={`/collections/${category.slug}`}
                            onClick={(e) => moved && e.preventDefault()}
                            className="flex flex-col items-center group flex-shrink-0"
                        >
                            {/* Icon Container */}
                            <div
                                className={`w-[76px] h-[76px] rounded-[14px] flex items-center justify-center mb-2 transition-all overflow-hidden p-0 ${isActive
                                    ? 'bg-[#FFF0F5] border-2 border-[#E80245]'
                                    : 'bg-[#F5F5F5] hover:scale-105'
                                    }`}
                            >
                                {category.featuredAsset?.preview ? (
                                    <img
                                        src={`${category.featuredAsset.preview}?w=80&h=80&format=webp`}
                                        alt={category.name}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                                        <span className="text-gray-400 text-[10px]">No img</span>
                                    </div>
                                )}
                            </div>

                            {/* Category Name */}
                            <span
                                className={`text-xs font-medium text-center line-clamp-2 w-[76px] leading-tight ${isActive ? 'text-[#E80245]' : 'text-[#1A1A1A]'
                                    }`}
                                style={{ fontFamily: 'Poppins, sans-serif' }}
                            >
                                {category.name}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
